import { DataSourceWithGenerics, FiltersPagingAndQuery, IndexToAggregates, SearchQuery, SearchQueryFn } from "@me8eon/data_sources";
import { TimeService } from "@me8eon/time";
import { Axios } from "axios";
import { TimeFilters } from "@me8eon/time_filters";
import { SearchTextFilter, searchTextName } from "@me8eon/searchtext_filters";
import { findTimeFilter, findRestFilters, findCompanyFilter } from "./elastic.search.time.filter";
import { composeElasticSearchFilters } from "./elastic.search.filters";
import { findSearchTextFilter } from "./elastic.searchtext.filter";
import { DataHasType } from "@me8eon/data";
import { aggregateAllQuerys, queryForTheAggregateFilters } from "./aggregate.query.generator";
import { nameWithOptions } from "@me8eon/string_utils";
import { NameAnd } from "@me8eon/record_utils";
import { DebugLog } from "@me8eon/debug";
import { JwtTokenFn } from "@me8eon/authentication";
import { newBoostFunctionScore, BoostIndexConfig, NewFunctionArr, FunctionScoreResult } from "./elastic.index.boosting";
import { useUserData } from "@me8eon/react_authentication";
import { CompanyFilters } from "@me8eon/company_filters";

export const elasticSearch = "elasticSearch";
export const elasticSearchAggregates = "elasticSearchAggregates";


export type ElasticSearchContext = {
    timeService: TimeService,
    elasticSearchUrl: string
    axios: Axios
    token: JwtTokenFn
    indexBoosts: BoostIndexConfig
}
export type ElasticSearchFilters = TimeFilters & SearchTextFilter & CompanyFilters
export type ElasticsearchQuery = {
    url: string,
    body: any
};
export type ElasticSearchResponse = {
    hits: {
        total: {
            value: number
        }
        hits: any[]
    },
    aggregations:
    NameAnd<ElasticSearchAggregationResult> //this name is the index

}
export type ElasticSearchAggregationResult = NameAnd<OneAggregateResult> //this name is the name of the aggregate

export type OneAggregateResult = {
    buckets: { key: string, doc_count: number }[]
}

export type ElasticSearchDevMode = ElasticsearchQuery & {
    response: ElasticSearchResponse
}


export type MakeElasticSearchQueryFromFilters<Filters extends ElasticSearchFilters> = (
    context: ElasticSearchContext,
    filters: ElasticSearchFilters,
    name: string[],
    scroll: boolean,
) => FiltersPagingAndQuery<Filters, ElasticSearchPaging, ElasticsearchQuery>;

export const sort = [
    {
        _score: {
            order: "desc",
        },
    },
    {
        _id: {
            order: "asc",
        },
    },
];

export const makeElasticSearchQueryFromFilters = <Filters extends ElasticSearchFilters>(context: ElasticSearchContext): SearchQueryFn<Filters, ElasticSearchPaging, ElasticsearchQuery> => {
    // const findBoosts = boostFunctionScore(context.indexBoosts);
    // const findBoosts = newBoostFunctionScore(context.indexBoosts);
    return ({ filters, names, scroll, paging, debug, userData }: SearchQuery<Filters, ElasticSearchPaging>): FiltersPagingAndQuery<Filters, ElasticSearchPaging, ElasticsearchQuery> => {
        const timeFilter = findTimeFilter(context, filters, "last_updated");
        const keywordFilter = findSearchTextFilter(filters);
        const search_after = scroll && paging ? { search_after: paging } : {};
        const nameAndOptions = names.map(nameWithOptions);
        const restFilter = findRestFilters(filters);
        const allOptions = [...new Set(nameAndOptions.flatMap(x => x.options))].sort();
        const aggFilters = queryForTheAggregateFilters(allOptions, filters);
        const companyFilter = findCompanyFilter(context, filters, "companyIdTags", userData);

        // const boosts: NewFunctionArr = findBoosts(filters[searchTextName]);

        function generateScripts() {
            const scriptLines = [
                { _index: "confluence-prod", weight: 0.4 },
                // ...boosts
            ].map((item, i) => {
                const condition = i === 0
                    ? `if (doc['_index'].value == '${item._index}')`
                    : `else if (doc['_index'].value == '${item._index}')`;
                return `${condition} { return _score * ${item.weight}; }`;
            });
            scriptLines.push(`else { return _score; }`);
            const scriptSource = scriptLines.join('\n');
            return scriptSource;
        }

        function addBoosts(q: any) {
            // if (boosts.length === 0) return { query: q };
            return {
                query: {
                    function_score: {
                        query: q,
                        ...(filters.dataview === "all" ? {
                            script_score: {
                                script: {
                                    source: generateScripts(),
                                },
                            },
                            boost_mode: "replace",
                        } : {}),
                    },
                },
            };
        }

        const body = {
            ...addBoosts(composeElasticSearchFilters(restFilter, timeFilter, keywordFilter, companyFilter, ...aggFilters)),
            _source: {
                excludes: [
                    "full_text_embeddings",
                    "denied_countries",
                    "denied_companies",
                    "denied_users",
                    "allowed_countries",
                    "allowed_companies",
                    "allowed_users",
                    "allowed_all_countries",
                    "allowed_all_companies",
                    "allowed_all_users",
                    "allowed_empTypes",
                    "companyIdTags",
                    "companyTags"
                ],
            },
            sort,
            ...search_after,
        };
        const query: ElasticsearchQuery = {
            url: `${context.elasticSearchUrl}/${nameAndOptions.map(x => x.name).join(",")}/_search`,
            body: body,
        };
        return { filters, paging, query };
    };
};

export const elasticSearchSearchFn = (context: ElasticSearchContext) => async (query: ElasticsearchQuery): Promise<ElasticSearchDevMode> => {
    const { url, body } = query;
    const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${await context.token()}`,
    };
    const response = await context.axios.post(url, body, { headers });
    return { ...query, response: response.data };
};

export type ElasticSearchPaging = {
    id: string,
    score: number,
}


export function makeAggregatesFrom(response: ElasticSearchResponse): IndexToAggregates {
    const result: IndexToAggregates = {};

    for (const [indexName, aggResults] of Object.entries(response?.aggregations || {})) {
        result[indexName] = {};

        for (const [aggName, aggValue] of Object.entries(aggResults)) {
            if (typeof aggValue !== "object") continue;
            if (!("buckets" in aggValue)) continue;

            result[indexName][aggName] = {};
            for (const bucket of aggValue.buckets) {
                result[indexName][aggName][bucket.key] = bucket.doc_count;
            }
        }
    }

    return result;
}

function elasticSearchResults(d: ElasticsearchQuery & { response: ElasticSearchResponse }, source: string, debugLog: DebugLog) {
    const hits = d.response?.hits?.hits || [];
    const results = hits.map(({ _source = {}, ...rest }) => ({ ...rest, ..._source, source }));
    const paging = results[results.length - 1]?.sort;
    const finished = hits.length === 0;
    const aggregates = makeAggregatesFrom(d.response);
    return { paging, results, finished, aggregates };
}

export function elasticSearchDs<Filters extends ElasticSearchFilters>(context: ElasticSearchContext): DataSourceWithGenerics<Filters, ElasticSearchPaging, ElasticsearchQuery, ElasticSearchDevMode, DataHasType[]> {
    return {
        plugin: "datasource",
        type: elasticSearch,
        query: makeElasticSearchQueryFromFilters(context),
        search: elasticSearchSearchFn(context),
        results: elasticSearchResults,
    };

}


export const makeElasticSearchQueryForAggregatesDS = <Filters extends ElasticSearchFilters>(context: ElasticSearchContext): SearchQueryFn<Filters, ElasticSearchPaging, ElasticsearchQuery> =>
    ({ filters, names, scroll, paging, debug, userData }: SearchQuery<Filters, ElasticSearchPaging>,
    ): FiltersPagingAndQuery<Filters, ElasticSearchPaging, ElasticsearchQuery> => {
        const nameAndOptions = names.map(nameWithOptions);
        const aggregations = scroll ? {} : { aggregations: aggregateAllQuerys(nameAndOptions) };
        const timeFilter = findTimeFilter(context, filters, "last_updated");
        const restFilter = findRestFilters(filters);
        const keywordFilter = findSearchTextFilter(filters);
        const companyFilter = findCompanyFilter(context, filters, "companyIdTags", userData);

        const query: ElasticsearchQuery = {
            url: `${context.elasticSearchUrl}/${nameAndOptions.map(x => x.name).join(",")}/_search`,
            body: {
                query: composeElasticSearchFilters(restFilter, timeFilter, keywordFilter, companyFilter),
                _source: {
                    excludes: [
                        "full_text_embeddings",
                        "denied_countries",
                        "denied_companies",
                        "denied_users",
                        "allowed_countries",
                        "allowed_companies",
                        "allowed_users",
                        "allowed_all_countries",
                        "allowed_all_companies",
                        "allowed_all_users",
                        "allowed_empTypes",
                        "companyIdTags",
                        "companyTags"
                    ],
                },
                size: 0,
                ...aggregations,
            },
        };
        return { filters, paging, query };
    };

export function elasticSearchForAggregatesDs<Filters extends ElasticSearchFilters>(context: ElasticSearchContext): DataSourceWithGenerics<Filters, ElasticSearchPaging, ElasticsearchQuery, ElasticSearchDevMode, DataHasType[]> {
    return {
        plugin: "datasource",
        type: elasticSearchAggregates,
        query: makeElasticSearchQueryForAggregatesDS(context),
        search: elasticSearchSearchFn(context),
        results: elasticSearchResults,
    };

}
